import React, { useState, useEffect, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { CircularProgress } from '@mui/material';
import { UserContext } from './UserContext';
import { getDefaultRouteForRole } from 'src/routes/defaultRouteByRole';
import { getStoredAuthValue, setAuthMetaValue, updateStoredAuthTokens } from './authStorage';

const hasValidTenantId = (tenantId) => {
  const value = String(tenantId ?? '').trim();
  return value !== '' && value.toLowerCase() !== 'null' && value.toLowerCase() !== 'undefined';
};

const resolveStoredBoolean = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return null;
};

export const isTokenExpired = (token) => {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000; // in seconds
    return decodedToken.exp < currentTime;
};

export const refreshToken = async () => {
  try {
      const currentRefreshToken = getStoredAuthValue('refreshToken');
      if (!currentRefreshToken) {
        throw new Error('No refresh token found');
      } 
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/refresh-tokens`, {
        refreshToken: currentRefreshToken,
      });
      updateStoredAuthTokens({ accessToken: data.access.token, refreshToken: data.refresh.token });
      return data.access.token;
  } catch (error) {
      console.error("Refresh token error", error);
      throw error;
  }
};

const renderLoader = () => (
  <div style={{height:"90vh", display:"flex", justifyContent:"center", alignItems:'center'}}>
    <CircularProgress/>
  </div>
);

const BILLING_ALLOWED_ROUTE = '/settings/subscription';

const deriveEffectiveSubscriptionStatus = (subscription) => {
  if (!subscription) return 'restricted';

  const baseStatus = String(subscription.status || '').toLowerCase();
  if (['restricted', 'expired', 'cancelled', 'suspended'].includes(baseStatus)) {
    return baseStatus;
  }

  const now = Date.now();
  const currentPeriodEnd = subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).getTime() : null;
  const gracePeriodEnd = subscription.gracePeriodEnd ? new Date(subscription.gracePeriodEnd).getTime() : null;

  if (currentPeriodEnd && currentPeriodEnd <= now) {
    if (gracePeriodEnd && gracePeriodEnd > now) return 'grace';
    return 'restricted';
  }

  return baseStatus || 'active';
};

const AuthRoute = ({ element, requiredRole, unauthorizedRedirect }) => {
  const [isAuth, setIsAuth] = useState(null);
  const [companySubscriptionStatus, setCompanySubscriptionStatus] = useState('');
  const [subscriptionGate, setSubscriptionGate] = useState({ loading: true, blocked: false });
  const location = useLocation();
  const { user } = useContext(UserContext);
  const storedRole = getStoredAuthValue('userRole');
  const storedTenantId = getStoredAuthValue('tenantID');
  const storedIsOnboardingComplete = getStoredAuthValue('isOnboardingComplete');
  const storedSubscriptionStatus = String(getStoredAuthValue('subscriptionStatus') || '').trim().toLowerCase();
  const effectiveRole = user?.role || storedRole;
  const effectiveTenantId = user?.tenantId || storedTenantId;
  const effectiveIsOnboardingComplete =
    typeof user?.isOnboardingComplete === 'boolean'
      ? user.isOnboardingComplete
      : resolveStoredBoolean(storedIsOnboardingComplete);
  const isTenantCreateRoute = location.pathname === '/tenant/create';
  const isSubscriptionRoute = location.pathname === BILLING_ALLOWED_ROUTE;

    useEffect(() => {
    const accessToken = getStoredAuthValue('accessToken');
    const currentRefreshToken = getStoredAuthValue('refreshToken');

    if (!accessToken || !currentRefreshToken) {
      setIsAuth(false);
      return;
    }

    setIsAuth(true);
  }, [location]);

  useEffect(() => {
    if (!isAuth || !hasValidTenantId(effectiveTenantId) || isTenantCreateRoute) {
      setCompanySubscriptionStatus('');
      return;
    }

    let active = true;

    const resolveSubscriptionStatus = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/config`, {
          params: { tenant_id: effectiveTenantId },
        });
        const nextStatus = String(data?.subscriptionStatus || '').trim().toLowerCase();
        setAuthMetaValue('subscriptionStatus', nextStatus);
        if (active) {
          setCompanySubscriptionStatus(nextStatus);
        }
      } catch (error) {
        if (active) {
          setCompanySubscriptionStatus(storedSubscriptionStatus || '');
        }
      }
    };

    resolveSubscriptionStatus();
    return () => {
      active = false;
    };
  }, [effectiveTenantId, isAuth, isTenantCreateRoute, storedSubscriptionStatus]);

  useEffect(() => {
    let active = true;

    const checkSubscription = async () => {
      if (!isAuth || !hasValidTenantId(effectiveTenantId) || isTenantCreateRoute) {
        if (active) {
          setSubscriptionGate({ loading: false, blocked: false });
        }
        return;
      }

      try {
        if (active) {
          setSubscriptionGate((prev) => ({ ...prev, loading: true }));
        }
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/billing/subscriptions/current`);
        const effectiveStatus = String(data?.effectiveStatus?.status || '').toLowerCase()
          || deriveEffectiveSubscriptionStatus(data?.subscription);
        const blocked = typeof data?.effectiveStatus?.billingOnly === 'boolean'
          ? data.effectiveStatus.billingOnly
          : ['restricted', 'expired', 'cancelled', 'suspended'].includes(effectiveStatus);
        if (active) {
          setSubscriptionGate({ loading: false, blocked });
        }
      } catch (error) {
        if (active) {
          setSubscriptionGate({ loading: false, blocked: false });
        }
      }
    };

    checkSubscription();
    return () => {
      active = false;
    };
  }, [effectiveTenantId, isAuth, isTenantCreateRoute, location.pathname]);

    if (isAuth === null) {
    return renderLoader();
    };
  if (!isAuth) {
    return <Navigate to="/auth/login" replace />;
  }

  if (!isTenantCreateRoute) {
    if (!effectiveRole) {
      return renderLoader();
    }
    if (!hasValidTenantId(effectiveTenantId)) {
      return <Navigate to="/auth/register/company" replace />;
    }
    if (companySubscriptionStatus === 'inactive' && effectiveIsOnboardingComplete !== true) {
      return <Navigate to="/auth/register/plans" replace />;
    }
  }

  if (requiredRole) {
    if (!effectiveRole) {
      return renderLoader();
    }

    if (effectiveRole !== requiredRole) {
      const redirectPath = unauthorizedRedirect || getDefaultRouteForRole(effectiveRole);
      return <Navigate to={redirectPath} replace />;
    }
  }

  if (subscriptionGate.loading && hasValidTenantId(effectiveTenantId) && !isTenantCreateRoute) {
    return renderLoader();
  }

  if (subscriptionGate.blocked && !isSubscriptionRoute) {
    return <Navigate to={BILLING_ALLOWED_ROUTE} replace />;
  }

  return element;
};

export default AuthRoute;
